import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import LoadingState from "../../components/ui/loading-state";
import { TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta, KartuBarisAksi } from "../../components/ui/responsive-table";
import { formatTanggal } from "../../lib/tanggal";
import { ArrowLeft, Upload, FileDown, AlertTriangle, Loader2, EyeOff, Eye, Tags } from "lucide-react";
import { MDR_BASE, MODUL_MDR } from "../../lib/claimMdr";

/**
 * Master SRP Promo (brief 22 Sep butir 4, 5, 15).
 *
 * Koleksinya **APPEND-ONLY**: impor periode baru hanya MENAMBAH baris, tidak
 * pernah mengubah atau menutup baris lama (butir 15 melarangnya karena nilai
 * SRP transaksi yang sudah diproses bisa bergeser diam-diam). Jadi daftar ini
 * sekaligus riwayat yang diminta butir 5.
 *
 * Satu-satunya yang bisa dilakukan pada baris lama adalah MENONAKTIFKANnya —
 * dan itu bukan kenyamanan: kalau dua baris dengan nilai berbeda sama-sama
 * berlaku, `cari_srp` menolak menebak dan seluruh nota tipe itu macet di
 * "Perlu Review". Tanpa tombol ini, satu salah ketik SRP tidak punya jalan
 * keluar sama sekali.
 */
export default function ClaimMdrSrp() {
  const { can } = useAuth();
  const bolehLihat = can(MODUL_MDR, "view");
  const bolehUbah = can(MODUL_MDR, "update");
  const inputRef = useRef(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reload, setReload] = useState(0);
  const muatUlang = useCallback(() => setReload((n) => n + 1), []);
  const [riwayat, setRiwayat] = useState(false);
  const [sibuk, setSibuk] = useState("");
  const [hasilImpor, setHasilImpor] = useState(null);

  useEffect(() => {
    if (!bolehLihat) return;
    let batal = false;
    setLoading(true); setLoadError(null);
    (async () => {
      try {
        const r = await api.get(`${MDR_BASE}/srp`, { params: { termasuk_nonaktif: riwayat } });
        if (!batal) setItems(r.data.items || []);
      } catch (e) {
        if (!batal) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat Master SRP"); }
      } finally { if (!batal) setLoading(false); }
    })();
    return () => { batal = true; };
  }, [bolehLihat, riwayat, reload]);

  const unduhTemplate = async () => {
    setSibuk("template");
    try {
      const res = await api.get(`${MDR_BASE}/srp/template`, { responseType: "blob" });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = "template-master-srp-promo.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunduh template");
    } finally { setSibuk(""); }
  };

  const impor = async (berkas) => {
    if (!berkas) return;
    setSibuk("impor"); setHasilImpor(null);
    try {
      const fd = new FormData();
      fd.append("berkas", berkas);
      const r = await api.post(`${MDR_BASE}/srp/impor`, fd);
      setHasilImpor(r.data);
      if (r.data.masuk) toast.success(`${r.data.masuk} baris Master SRP masuk`);
      else toast.warning(r.data.pesan || "Tidak ada baris baru — semuanya sudah ada");
      muatUlang();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengimpor Master SRP");
    } finally {
      setSibuk("");
      if (inputRef.current) inputRef.current.value = "";   // supaya berkas sama bisa dipilih lagi
    }
  };

  const ubahNonaktif = async (baris, nonaktif) => {
    setSibuk(`baris-${baris.kunci}`);
    try {
      await api.patch(`${MDR_BASE}/srp/${baris.kunci}`, { nonaktif });
      toast.success(nonaktif ? "Baris dinonaktifkan" : "Baris diaktifkan kembali");
      muatUlang();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah baris");
    } finally { setSibuk(""); }
  };

  const periode = (b) =>
    `${formatTanggal(b.periode_dari)} – ${b.periode_sampai ? formatTanggal(b.periode_sampai) : "seterusnya"}`;

  const jumlahNonaktif = useMemo(() => items.filter((b) => b.is_deleted).length, [items]);

  if (!bolehLihat) {
    return (
      <div className="p-6" data-testid="mdr-srp-tanpa-izin">
        <p className="text-sm text-slate-600">Kamu belum punya izin membuka Master SRP Promo.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="mdr-srp-halaman">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/finance/claim-mdr" data-testid="mdr-srp-kembali"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
            <ArrowLeft className="w-3.5 h-3.5" /> Claim MDR
          </Link>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2 mt-0.5">
            <Tags className="w-5 h-5 text-slate-400" /> Master SRP Promo
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Dipakai bersama tab TAM & GDN. Baris lama tidak pernah diubah impor baru —
            daftar ini sekaligus riwayatnya.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setRiwayat((v) => !v)} data-testid="mdr-srp-riwayat"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50">
            {riwayat ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {riwayat ? "Sembunyikan nonaktif" : "Tampilkan riwayat"}
          </button>
          <button onClick={unduhTemplate} disabled={!!sibuk} data-testid="mdr-srp-template"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {sibuk === "template" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            Unduh Template
          </button>
          {bolehUbah && (
            <>
              <input ref={inputRef} type="file" accept=".xlsx,.xlsm,.xls" className="hidden"
                data-testid="mdr-srp-berkas"
                onChange={(e) => impor(e.target.files?.[0])} />
              <button onClick={() => inputRef.current?.click()} disabled={!!sibuk}
                data-testid="mdr-srp-impor"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
                {sibuk === "impor" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Impor Master SRP
              </button>
            </>
          )}
        </div>
      </div>

      {hasilImpor && (
        <div className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2"
          data-testid="mdr-srp-hasil">
          <p className="text-sm text-slate-800">
            <b>{hasilImpor.masuk || 0}</b> baris baru
            {hasilImpor.duplikat ? <> · {hasilImpor.duplikat} sudah ada</> : null}
            {hasilImpor.galat?.length ? <> · <span className="text-rose-700">{hasilImpor.galat.length} baris ditolak</span></> : null}
          </p>
          {hasilImpor.galat?.length > 0 && (
            <ul className="max-h-32 overflow-y-auto space-y-0.5">
              {hasilImpor.galat.map((g, i) => (
                <li key={i} className="text-[11px] text-rose-700">Baris {g.baris}: {g.pesan}</li>
              ))}
            </ul>
          )}
          {/* Tabrakan periode = PERINGATAN, bukan penolakan: brief melarang impor
              baru MENGUBAH periode lama, bukan melarang periode bertindih. Yang
              bertindih nanti terbaca "Perlu Review" saat pencocokan. */}
          {hasilImpor.tabrakan?.length > 0 && (
            <div data-testid="mdr-srp-tabrakan">
              <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                {hasilImpor.tabrakan.length} baris periodenya bertindih dengan nilai berbeda
              </p>
              <p className="text-[11px] text-amber-700 mt-0.5">
                Nota pada periode yang bertindih akan masuk “Perlu Review” — nilainya tidak
                ditebak. Nonaktifkan baris yang tidak berlaku supaya pencocokan jalan lagi.
              </p>
              <ul className="mt-1 space-y-0.5">
                {hasilImpor.tabrakan.map((t, i) => (
                  <li key={i} className="text-[11px] text-amber-700">
                    {t.tipe_produk} · mulai {formatTanggal(t.periode_dari)} bertindih dengan
                    baris mulai {formatTanggal(t.bertabrakan_dengan)} (SRP {idr(t.srp_lain)})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-slate-500" data-testid="mdr-srp-hitung">
        {items.length} baris{riwayat && jumlahNonaktif > 0 ? ` · ${jumlahNonaktif} nonaktif` : ""}
      </p>

      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <TabelDesktop>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Tipe Produk</th>
                <th className="px-3 py-2 text-left font-medium">Tingkat</th>
                <th className="px-3 py-2 text-right font-medium">SRP Promo</th>
                <th className="px-3 py-2 text-left font-medium">Periode Berlaku</th>
                <th className="px-3 py-2 text-left font-medium">Sumber</th>
                {bolehUbah && <th className="px-3 py-2 text-right font-medium">Aksi</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <LoadingState loading={loading} error={loadError} kosong={!loading && items.length === 0}
                onRetry={muatUlang} varian="tabel" kolom={bolehUbah ? 6 : 5}
                pesanKosong="Belum ada Master SRP Promo — impor berkasnya dulu."
                data-testid="mdr-srp-state">
                {items.map((b) => (
                  <tr key={b.kunci} className={b.is_deleted ? "bg-slate-50/60 text-slate-400" : "hover:bg-slate-50/60"}>
                    <td className="px-3 py-2 max-w-[20rem] truncate" title={b.tipe_produk || ""}>
                      {b.tipe_produk || "—"}
                      {b.is_deleted && <span className="ml-1.5 text-[10px] uppercase">nonaktif</span>}
                    </td>
                    <td className="px-3 py-2">
                      <TingkatPil berwarna={b.berwarna} />
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{idr(b.srp)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{periode(b)}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-500 max-w-[14rem] truncate"
                      title={b.sumber_berkas || ""}>
                      {b.sumber_berkas || "—"}
                    </td>
                    {bolehUbah && (
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => ubahNonaktif(b, !b.is_deleted)}
                          disabled={sibuk === `baris-${b.kunci}`}
                          data-testid={`mdr-srp-nonaktif-${b.kunci}`}
                          className="px-2 py-1 rounded border border-slate-200 bg-white text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                          {b.is_deleted ? "Aktifkan" : "Nonaktifkan"}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </LoadingState>
            </tbody>
          </table>
        </TabelDesktop>

        <DaftarKartuMobile testid="mdr-srp-kartu">
          <LoadingState loading={loading} error={loadError} kosong={!loading && items.length === 0}
            onRetry={muatUlang} varian="kartu"
            pesanKosong="Belum ada Master SRP Promo — impor berkasnya dulu."
            data-testid="mdr-srp-state-m">
            {items.map((b) => (
              <KartuBaris key={b.kunci} testid={`mdr-srp-card-${b.kunci}`}
                className={b.is_deleted ? "bg-slate-50/60" : undefined}>
                <KartuBarisHeader
                  kiri={
                    <>
                      <p className={`text-sm font-semibold truncate ${b.is_deleted ? "text-slate-400" : "text-slate-900"}`}>
                        {b.tipe_produk || "—"}
                      </p>
                      <p className="text-[11px] text-slate-500">{periode(b)}</p>
                    </>
                  }
                  kanan={<TingkatPil berwarna={b.berwarna} />}
                />
                <KartuBarisMeta label="SRP Promo" value={idr(b.srp)} tebal />
                <KartuBarisMeta label="Sumber" value={b.sumber_berkas} />
                <KartuBarisMeta label="Keadaan" value={b.is_deleted ? "Nonaktif" : "Aktif"} />
                {bolehUbah && (
                  <KartuBarisAksi>
                    <button onClick={() => ubahNonaktif(b, !b.is_deleted)}
                      disabled={sibuk === `baris-${b.kunci}`}
                      data-testid={`mdr-srp-nonaktif-${b.kunci}-m`}
                      className="px-2 py-1 rounded border border-slate-200 bg-white text-[11px] font-medium text-slate-600 disabled:opacity-50">
                      {b.is_deleted ? "Aktifkan" : "Nonaktifkan"}
                    </button>
                  </KartuBarisAksi>
                )}
              </KartuBaris>
            ))}
          </LoadingState>
        </DaftarKartuMobile>
      </div>
    </div>
  );
}

/** "Per warna" vs "Umum" — nilainya DIHITUNG SERVER (`srp.berwarna`), bukan
 *  disimpulkan di layar dari ada-tidaknya koma. Baris per warna MENANG atas
 *  baris umum saat pencocokan (jawaban Bos no. 37), jadi bedanya menentukan
 *  uang dan harus terlihat. */
function TingkatPil({ berwarna }) {
  if (berwarna === undefined || berwarna === null) return <span className="text-slate-400">—</span>;
  return berwarna ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 text-[11px] font-medium"
      title="Berlaku HANYA untuk warna ini, dan menang atas baris umum">
      Per warna
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[11px] font-medium"
      title="Berlaku untuk semua warna model+storage ini">
      Umum
    </span>
  );
}
